import { Bookmark } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useWishlist } from "@/hooks/useWishlist";
import { shopifyImageUrl, shopifyImageSrcSet } from "@/lib/shopify-image";

interface ProductCardProps {
  isLatest?: boolean;
  title?: string;
  price?: string;
  imageUrl?: string;
  imageAlt?: string;
  handle?: string;
}

const ProductCard = ({ isLatest = false, title, price, imageUrl, imageAlt, handle }: ProductCardProps) => {
  const { t } = useLanguage();
  const { isWishlisted, toggle, isLoggedIn } = useWishlist();
  const saved = handle ? isWishlisted(handle) : false;

  const handleBookmark = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isLoggedIn || !handle) return;
    toggle(handle);
  };

  const content = (
    <div className="group cursor-pointer">
      <div className="relative aspect-square media-placeholder overflow-hidden">
        {imageUrl && (
          <img
            src={shopifyImageUrl(imageUrl, { width: 600 }) || imageUrl}
            srcSet={shopifyImageSrcSet(imageUrl, [240, 360, 480, 600, 800]) || undefined}
            sizes="(min-width: 1024px) 280px, (min-width: 768px) 33vw, 50vw"
            alt={imageAlt || title || "Product"}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            decoding="async"
          />
        )}
        {isLatest && (
          <div className="absolute top-2.5 left-2.5 z-10">
            <span className="inline-block px-2 py-0.5 bg-muted/90 text-foreground text-meta border border-border/50">
              {t("product.latest")}
            </span>
          </div>
        )}
        <div className="absolute top-2.5 right-2.5 z-10">
          <button
            onClick={handleBookmark}
            aria-label={saved ? t("product.removeFromWishlist") || "Remove from wishlist" : t("product.addToWishlist") || "Add to wishlist"}
            aria-pressed={saved}
            className="w-8 h-8 flex items-center justify-center bg-background/60"
          >
            <Bookmark
              size={16}
              strokeWidth={1.3}
              className={saved ? "text-accent" : "text-secondary-foreground group-hover:text-foreground transition-colors"}
              fill={saved ? "currentColor" : "none"}
            />
          </button>
        </div>
      </div>
      <h2 className="product-item__title text-foreground text-center mt-3.5 mb-1.5 leading-snug">
        {title || "space filler"}
      </h2>
      <div className="product-information">
        <p className="price text-secondary-foreground text-center">
          {price || "space filler"}
        </p>
      </div>
    </div>
  );

  if (handle) {
    return <Link to={`/product/${handle}`}>{content}</Link>;
  }

  return content;
};

export default ProductCard;
